import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { CSVLink } from "react-csv";
import { Switch } from "antd";

import axios from "axios";
import { Baseurl } from "../../config/BaseUrl";

const GetAllCase = () => {
  const { totalAdmins } = useSelector((store) => store.auth);
  const { caseList } = useSelector((store) => store.cases);
  const { employeeList } = useSelector((store) => store.employee);
  const { loginData } = useSelector((store) => store.auth);
  const [search, setSearch] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [urlPath, setUrlPath] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [filteredCase, setFilteredCase] = useState([]);
  const [filteredEmployee, setFilteredEmployee] = useState([]);
  const [filteredForEmployee, setFilteredForEmployee] = useState([]);
  const [filterName, setFilterName] = useState("");
  const [loadData, setLoadData] = useState([]);
  const [role, setRole] = useState("");
  const [name, setName] = useState("");
  
  // Pagination states
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [totalCases, setTotalCases] = useState(0);
  const [limit] = useState(20); // Items per page

  // Initial data fetch
  useEffect(() => {
    fetchCases(1, true);
  }, [startDate, endDate, loginData]); // Re-fetch when filters change

  const fetchCases = async (pageNumber, reset = false) => {
    if (loading) return;
    
    setLoading(true);
    try {
      let url = `${Baseurl}/api/v1/cases/getPaginatedCases?page=${pageNumber}&limit=${limit}`;
      
      // Add date filters if they exist
      if (startDate && endDate) {
        url += `&startDate=${startDate}&endDate=${endDate}`;
      }
      
      const resp = await axios.get(url);
      
      if (resp.data.success) {
        const { cases, totalCases: total, currentPage, totalPages } = resp.data;
        
        let processedCases = cases;
        
        // Apply business associate filter if needed
        if (loginData?.role === "businessAssociate") {
          processedCases = cases.filter((indCase) =>
            loginData.permittedState.includes(indCase.state),
          );
        }
        
        if (reset) {
          setFilteredData(processedCases);
          setFilteredCase(processedCases);
        } else {
          setFilteredData(prev => [...prev, ...processedCases]);
          setFilteredCase(prev => [...prev, ...processedCases]);
        }
        
        setTotalCases(total);
        setHasMore(pageNumber < totalPages);
        setPage(pageNumber);
        
        // Update CSV data
        updateCSVData(reset ? processedCases : [...filteredData, ...processedCases]);
      }
    } catch (error) {
      console.error("Error fetching cases:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateCSVData = (cases) => {
    let finalexport = [];
    
    cases.forEach((element, index) => {
      const currentDate = new Date(element.submittedAt);
      const createdDate = new Date(element.createdAt);

      const formdata = {
        No: index + 1,
        "Case Id": element.caseId,
        Name: element.name,
        Mobile: element.mobile,
        Email: element.email,
        "Client Name": element.requesterName,
        "Client Branch": element.requesterBranch,
        "Client Phone": element.requesterPhone,
        "Loan No": element.loanAgreementNo,
        "Case Type": element.caseType,
        "Vehicle Type": element.assetType,
        "Vehicle Make": element.brand,
        "Vehicle Model": element.model,
        "Vehicle Variant": element.variant,
        "Reg No.": element.regdNo,
        "Chasis No.": element.chasisNo,
        "Engine No.": element.engNo,
        "Meter Reading": element.meterReading,
        "Date Of Reg": element.regdDate,
        "Fuel Type": element.fueltype,
        "Vehicle Color": element.vehicleColor,
        "Number Plate": element.numberPlate,
        "Insurance Expiry": element.otherDetails?.insuranceExpiry || "",
        "Vehicle Exshowroom Price": element.marketPrice,
        "Estimated Market Price": element.valuation,
        "Vehicle Location": element.vehicleLocation,
        "Vehicle City": element.city,
        "Vehicle State": element.state,
        "Case Created Date": createdDate.toDateString(),
        "Case Created Time": createdDate.toLocaleTimeString(),
        "Field Service By": element.fieldServiceBy,
        "Field Service Done Date": currentDate.toDateString(),
        "Field Service Done Time": currentDate.toLocaleTimeString(),
        "Field Service Remark": element.yourRemark,
        paymentStatus: element.paymentStatus,
        paymentRemark: element.paymentRemark,
        "Pdf Report Url": element.reportUrl,
        "Selfie With Vehicle": element.selfieWithVehicle,
        "Video Url": element.videos,
        fieldServiceByName: element.fieldServiceBy,
      };
      
      finalexport.push(formdata);
    });
    
    setLoadData(finalexport);
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      fetchCases(page + 1);
    }
  };

  const handleStartDateChange = (e) => {
    const date = e.target.value;
    setStartDate(date);
    // Reset and fetch new data with filter
    fetchCases(1, true);
  };

  const handleEndDateChange = (e) => {
    const date = e.target.value;
    setEndDate(date);
    // Reset and fetch new data with filter
    fetchCases(1, true);
  };

  // Filter employees for dropdowns (keep existing)
  useEffect(() => {
    const uniqueEmployees = removeDuplicateEmployees(employeeList);
    setFilteredEmployee(uniqueEmployees);
  }, [employeeList]);

  const removeDuplicateEmployees = (employees) => {
    const roleMap = new Map();
    const uniqueEmployees = [];

    employees.forEach((employee) => {
      const role = employee.role;
      if (!roleMap.has(role)) {
        roleMap.set(role, true);
        uniqueEmployees.push(employee);
      }
    });

    return uniqueEmployees;
  };

  const selectRoleChange = (e) => {
    const { value } = e.target;
    const index = e.nativeEvent.target.selectedIndex;
    const text = e.nativeEvent.target[index].text;
    setRole(text);
    const employeeNew = employeeList.filter(
      (individualEmployee) => individualEmployee.role === text,
    );
    setFilteredForEmployee(employeeNew);
  };

  const selectNameChange = (e) => {
    const { value } = e.target;
    const index = e.nativeEvent.target.selectedIndex;
    const text = e.nativeEvent.target[index].text;
    setName(text);
    const employeeNew = employeeList.filter(
      (individualEmployee) => individualEmployee.name === text,
    );
    setFilterName(employeeNew);
  };

  return (
    <>
      <div
        className="min-h-screen flex flex-col lg:ps-64 w-full"
        style={{ marginBottom: "60px" }}
      >
        <div className="p-3 space-y-6">
          <div className="flex items-center justify-between w-full print:hidden">
            <h4 className="text-lg font-semibold text-default-900">
              Dashboard
            </h4>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "20px",
              }}
            >
              {/* Start Date Input */}
              <label htmlFor="startDateInput">Start Date:</label>
              <input
                type="date"
                id="startDateInput"
                value={startDate}
                onChange={handleStartDateChange}
                style={{ borderColor: "#4D44B5", borderRadius: "5px" }}
              />

              {/* End Date Input */}
              <label htmlFor="endDateInput">End Date:</label>
              <input
                type="date"
                id="endDateInput"
                value={endDate}
                onChange={handleEndDateChange}
                style={{ borderColor: "#4D44B5", borderRadius: "5px" }}
              />
            </div>
            <div className="p-1">
              <CSVLink
                data={loadData}
                filename={"instavaluer-Submitted-data.csv"}
                onClick={() => {}}
              >
                <button
                  className="mt-0 btn btn-primary mb-2 download-responses-button"
                  style={{
                    background: "#3270fc",
                    border: "1px solid #3270fc",
                    padding: "12px",
                    color: "#fff",
                    borderRadius: "3px",
                  }}
                >
                  Download Report
                </button>
              </CSVLink>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1">
          <div className="shadow rounded-lg bg-white dark:bg-default-50">
            <div className="border-t border-dashed border-default-200">
              <InfiniteScroll
                dataLength={filteredData.length}
                next={loadMore}
                hasMore={hasMore}
                loader={
                  <div className="text-center py-4">
                    <h4>Loading more cases...</h4>
                  </div>
                }
                endMessage={
                  <div className="text-center py-4">
                    <p className="text-default-600">
                      {filteredData.length > 0 
                        ? "You have seen all cases" 
                        : "No cases found"}
                    </p>
                  </div>
                }
                scrollThreshold={0.9}
              >
                <div className="relative overflow-x-auto">
                  <table className="min-w-full overflow-x-hidden">
                    <thead className="border-b border-dashed border-default-200">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-2 text-center text-sm capitalize font-semibold text-default-900 min-w-16"
                        >
                          No
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-2 text-center text-sm capitalize font-semibold text-default-900 min-w-16"
                        >
                          Reg No.
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-2 text-center text-sm capitalize font-semibold text-default-900 min-w-16"
                        >
                          Requester Name
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-2 text-center text-sm capitalize font-semibold text-default-900 min-w-16"
                        >
                          Requester Branch
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-2 text-center text-sm capitalize font-semibold text-default-900 min-w-40"
                        >
                          Name
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-2 text-center text-sm capitalize font-semibold text-default-900 min-w-20"
                        >
                          Mobile
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-2 text-center text-sm capitalize font-semibold text-default-900 min-w-20"
                        >
                          Email
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-2 text-center text-sm capitalize font-semibold text-default-900 min-w-20"
                        >
                          Field Service By
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-2 text-center text-sm capitalize font-semibold text-default-900 min-w-20"
                        >
                          Create Date
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3 text-center text-sm capitalize font-semibold text-default-900 min-w-20"
                        >
                          Payment Status
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3 text-center text-sm capitalize font-semibold text-default-900 min-w-20"
                        >
                          Payment Remark
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-dashed divide-default-200">
                      {filteredData.map((admin, index) => (
                        <tr key={`${admin._id}-${index}`}>
                          <td
                            className="px-6 text-center text-default-600 font-semibold whitespace-nowrap"
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#f2f2f2" : "#fff",
                            }}
                          >
                            <b>{admin.caseId}</b>
                          </td>
                          <td
                            className="px-6 text-center text-default-600 font-semibold whitespace-nowrap"
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#f2f2f2" : "#fff",
                            }}
                          >
                            {admin.regdNo}
                          </td>
                          <td
                            className="px-6 text-center text-default-600 font-semibold whitespace-nowrap"
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#f2f2f2" : "#fff",
                            }}
                          >
                            {admin.requesterName}
                          </td>
                          <td
                            className="px-6 text-center text-default-600 font-semibold whitespace-nowrap"
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#f2f2f2" : "#fff",
                            }}
                          >
                            {admin.requesterBranch}
                          </td>
                          <td
                            className="px-6 text-center whitespace-nowrap"
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#f2f2f2" : "#fff",
                            }}
                          >
                            <h6 className="text-sm font-semibold text-default-700">
                              {admin.name.length > 30 ? (
                                <>{admin.name.slice(0, 30)}...</>
                              ) : (
                                <>{admin.name.slice(0, 30)}</>
                              )}
                            </h6>
                          </td>
                          <td
                            className="px-6 text-center text-default-600 font-medium whitespace-nowrap"
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#f2f2f2" : "#fff",
                            }}
                          >
                            {admin.mobile}
                          </td>
                          <td
                            className="px-6 text-center text-default-600 font-medium whitespace-nowrap"
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#f2f2f2" : "#fff",
                            }}
                          >
                            {admin.email.length > 18 ? (
                              <>{admin.email.slice(0, 18)}...</>
                            ) : (
                              <>{admin.email.slice(0, 18)}</>
                            )}
                          </td>
                          <td
                            className="px-6 text-center text-default-600 font-medium whitespace-nowrap"
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#f2f2f2" : "#fff",
                            }}
                          >
                            <div className="d-flex m-auto">
                              {admin.fieldServiceBy}
                            </div>
                          </td>
                          <td
                            className="px-6 text-center text-default-600 font-medium whitespace-nowrap"
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#f2f2f2" : "#fff",
                            }}
                          >
                            <div className="d-flex m-auto">
                              {admin.createdAt.slice(0, 10)}
                            </div>
                          </td>
                          <td
                            className="px-6 py-3 text-center text-default-600 font-medium whitespace-nowrap"
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#f2f2f2" : "#fff",
                            }}
                          >
                            <div className="flex items-center justify-center gap-2">
                              <Switch
                                checked={admin?.paymentStatus ? true : false}
                              />
                            </div>
                          </td>
                          <td
                            className="px-6 py-3 text-center text-default-600 font-medium whitespace-nowrap"
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#f2f2f2" : "#fff",
                            }}
                          >
                            <div className="d-flex m-auto">
                              {admin.paymentRemark}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </InfiniteScroll>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-6 py-3 px-6 border-t border-dashed border-default-200">
              <h6 className="text-default-600">
                Showing {filteredData.length} of {totalCases} cases
                {loading && " (Loading more...)"}
              </h6>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GetAllCase;